import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
} from "react-router-dom";
import { ConfigProvider } from "antd";
import MainLayout from "./components/utils/Layout/MainLayout";
import Login from "./components/screens/common/Login";
import Register from "./components/screens/common/Register";
import { useAppSelector, useAppDispatch } from "./redux/hooks";
import api from "./utils/api";
import { setUser, clearAuth } from "./redux/slices/authSlice";



// Lazy load components
const AdminDashboard = React.lazy(
  () => import("./components/screens/admin/Dashboard")
);
const AdminUsers = React.lazy(() => import("./components/screens/admin/Users"));
const AdminAreas = React.lazy(() => import("./components/screens/admin/Areas"));
const AdminReports = React.lazy(
  () => import("./components/screens/admin/Reports")
);
const AnnouncementsPage = React.lazy(
  () => import("./components/screens/admin/Announcement")
);
const AuditTrail = React.lazy(
  () => import("./components/screens/admin/AuditAndTrail")
);
const FlaggedContentModeration = React.lazy(
  () => import("./components/screens/admin/FlaggedContentModeration")
);
const VolunteerAssignment = React.lazy(
  () => import("./components/screens/admin/VolunteerAssignment")
);
const ProfessionalCollaboration = React.lazy(
  () => import("./components/screens/admin/ProfessionalCollaboration")
);
const AnalyticsDashboard = React.lazy(
  () => import("./components/screens/admin/AnalyticsDashboard")
);
const PoliticalParty = React.lazy(
  () => import("./components/screens/admin/PoliticalParty")
);
const PublicOfficials = React.lazy(
  () => import("./components/screens/admin/PublicOfficials")
);
const ConstituenciesType = React.lazy(() => import('./components/screens/admin/ConstituenciesType'));
const Constituencies = React.lazy(() => import('./components/screens/admin/Constituencies'));
const WorkAssignment = React.lazy(() => import('./components/screens/admin/WorkAssignment'));
const Categories = React.lazy(() => import('./components/screens/admin/Categories'));
const AdminFeedbackRatings = React.lazy(
  () => import("./components/screens/admin/FeedbackRatings")
);

const CitizenDashboard = React.lazy(
  () => import("./components/screens/citizen/Dashboard")
);
const CitizenRepresentatives = React.lazy(
  () => import("./components/screens/citizen/Representatives")
);
const CitizenMessages = React.lazy(
  () => import("./components/screens/citizen/Messages")
);
const LocalServicesDirectory = React.lazy(
  () => import("./components/screens/citizen/LocalServicesDirectory")
);
const RepresentativesByArea = React.lazy(
  () => import("./components/screens/citizen/RepresentativesByArea")
);


const FindYourRepresentative = React.lazy(
  () => import("./components/screens/citizen/FindYourRepresentative")
);

const FollowedRepresentatives = React.lazy(
  () => import("./components/screens/citizen/FollowedRepresentatives")
);
const CollaborationRequests = React.lazy(
  () => import("./components/screens/citizen/CollaborationRequests")
);
const CivicEducation = React.lazy(
  () => import("./components/screens/citizen/CivicEducation")
);
const FeedbackRating = React.lazy(
  () => import("./components/screens/citizen/FeedbackRating")
);
const VolunteerApplication = React.lazy(
  () => import("./components/screens/citizen/VolunteerApplication")
);

const RepresentativeDashboard = React.lazy(
  () => import("./components/screens/representative/Dashboard")
);
const RepresentativeComplaints = React.lazy(
  () => import("./components/screens/representative/Complaints")
);
const RepresentativeAnnouncements = React.lazy(
  () => import("./components/screens/representative/Announcements")
);
const FieldVisitNotes = React.lazy(
  () => import("./components/screens/representative/fieldVisitNote")
);
const VolunteerCoordination = React.lazy(
  () => import("./components/screens/representative/VolunteerCoordination")
);
const CalendarView = React.lazy(
  () => import("./components/screens/representative/calenderView")
);
const IssueManagement = React.lazy(
  () => import("./components/screens/representative/IssueManagement")
);
const RepresentativeProfileManagement = React.lazy(
  () => import("./components/screens/representative/ProfileManagement")
);
const CreatePost = React.lazy(
  () => import("./components/screens/citizen/CreatePost")
);
const VolunteerAreaUpdateRequests = React.lazy(
  () => import("./components/screens/volunteer/AreaUpdateRequests")
);
const AdminAreaUpdateRequests = React.lazy(
  () => import("./components/screens/admin/AreaUpdateRequests")
);
const VolunteerApplications = React.lazy(
  () => import("./components/screens/admin/VolunteerApplications")
);
const VolunteerTasks = React.lazy(
  () => import("./components/screens/admin/VolunteerTasks")
);
const VolunteerMyTasks = React.lazy(
  () => import("./components/screens/volunteer/MyTasks")
);

const Ministry = React.lazy(() => import("./components/screens/admin/Ministry"));

const PrivateRoute = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" />;
};

const AdminRoutes = () => (
  <Routes>
    <Route
      path="dashboard"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <AdminDashboard />
        </Suspense>
      }
    />
    <Route
      path="ContentModeration"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <FlaggedContentModeration />
        </Suspense>
      }
    />
    <Route
      path="VolunteerAssignment"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <VolunteerAssignment />
        </Suspense>
      }
    />
    <Route
      path="announcement"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <AnnouncementsPage />
        </Suspense>
      }
    />
    <Route
      path="users"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <AdminUsers />
        </Suspense>
      }
    />
    <Route
      path="areas"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <AdminAreas />
        </Suspense>
      }
    />
    <Route
      path="reports"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <AdminReports />
        </Suspense>
      }
    />
    <Route
      path="messages"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <CitizenMessages />
        </Suspense>
      }
    />
    <Route
      path="feedback-ratings"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <AdminFeedbackRatings />
        </Suspense>
      }
    />
    <Route
      path="auditLog"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <AuditTrail />
        </Suspense>
      }
    />
    <Route
      path="ProfessionalCollaboration"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <ProfessionalCollaboration />
        </Suspense>
      }
    />
    <Route
      path="AnalyticsDashboard"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <AnalyticsDashboard />
        </Suspense>
      }
    />
    <Route
      path="Ministry"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <Ministry />
        </Suspense>
      }
    />
    <Route
      path="PoliticalParty"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <PoliticalParty />
        </Suspense>
      }
    />
    <Route
      path="PublicOfficials"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <PublicOfficials />
        </Suspense>
      }
    />
    <Route
      path="work-assignment"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <WorkAssignment />
        </Suspense>
      }
    />
    <Route
      path="categories"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <Categories />
        </Suspense>
      }
    />
    <Route
      path="constituencies-type"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <ConstituenciesType />
        </Suspense>
      }
    />
    <Route
      path="constituencies"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <Constituencies />
        </Suspense>
      }
    />
    <Route
      path="area-update-requests"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <AdminAreaUpdateRequests />
        </Suspense>
      }
    />
    <Route
      path="volunteer-applications"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <VolunteerApplications />
        </Suspense>
      }
    />
    <Route
      path="volunteer-tasks"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <VolunteerTasks />
        </Suspense>
      }
    />
    <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
  </Routes>
);

const CitizenRoutes = ({ basePath = '/citizen', isVolunteer = false }) => {
  return (
    <Routes>
      <Route
        path="MyIssuesTracker"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <CitizenDashboard />
          </Suspense>
        }
      />
      <Route
        path="dashboard"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <CitizenDashboard />
          </Suspense>
        }
      />
      <Route
        path="representatives"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <CitizenRepresentatives />
          </Suspense>
        }
      />
      <Route
        path="messages"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <CitizenMessages />
          </Suspense>
        }
      />
      <Route

        path="FindYourRepresentative"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <FindYourRepresentative />
          </Suspense>
        }
      />
      <Route
        path="LocalServicesDirectory"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <LocalServicesDirectory />
          </Suspense>
        }
      />
      <Route
        path="RepresentativesByArea"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <RepresentativesByArea />
          </Suspense>
        }
      />
      <Route
        path="CollaborationRequests"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <CollaborationRequests />
          </Suspense>
        }
      />
      <Route
        path="FollowedRepresentatives"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <FollowedRepresentatives />
          </Suspense>
        }
      />

      <Route
        path="create-post"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <CreatePost />
          </Suspense>
        }
      />
      <Route
        path="civic-education"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <CivicEducation />
          </Suspense>
        }
      />
      <Route
        path="FeedbackRating"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <FeedbackRating />
          </Suspense>
        }
      />
      <Route
        path="volunteer-application"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <VolunteerApplication />
          </Suspense>
        }
      />
      {isVolunteer && (
        <Route
          path="area-update-requests"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <VolunteerAreaUpdateRequests />
            </Suspense>
          }
        />
      )}
      {isVolunteer && (
        <Route
          path="my-tasks"
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <VolunteerMyTasks />
            </Suspense>
          }
        />
      )}
      <Route path="*" element={<Navigate to={`${basePath}/dashboard`} replace />} />
    </Routes>
  );
};

const RepresentativeRoutes = () => (
  <Routes>
    <Route
      path="dashboard"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <RepresentativeDashboard />
        </Suspense>
      }
    />
    <Route
      path="complaints"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <RepresentativeComplaints />
        </Suspense>
      }
    />
    <Route
      path="announcements"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <RepresentativeAnnouncements />
        </Suspense>
      }
    />
    <Route
      path="messages"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <CitizenMessages />
        </Suspense>
      }
    />
    <Route
      path="CalendarView"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <CalendarView />
        </Suspense>
      }
    />
    <Route
      path="FieldVisitNotes"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <FieldVisitNotes />
        </Suspense>
      }
    />
    <Route
      path="VolunteerCoordination"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <VolunteerCoordination />
        </Suspense>
      }
    />
    <Route
      path="IssueManagement"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <IssueManagement />
        </Suspense>
      }
    />
    <Route
      path="profile"
      element={
        <Suspense fallback={<div>Loading...</div>}>
          <RepresentativeProfileManagement />
        </Suspense>
      }
    />
    <Route path="*" element={<Navigate to="/representative/dashboard" replace />} />
  </Routes>
);

const AppRoutes = () => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<PrivateRoute />}>
        <Route element={<MainLayout />}>
          {user?.role === "ADMIN" && (
            <Route path="/admin/*" element={<AdminRoutes />} />
          )}
          {user?.role === "CITIZEN" && (
            <Route path="/citizen/*" element={<CitizenRoutes basePath="/citizen" />} />
          )}
          {user?.role === "VOLUNTEER" && (
            <>
              <Route path="/volunteer/*" element={<CitizenRoutes basePath="/volunteer" isVolunteer />} />
              <Route path="/citizen/*" element={<CitizenRoutes basePath="/volunteer" isVolunteer />} />
            </>
          )}
          {user?.role === "REPRESENTATIVE" && (
            <Route
              path="/representative/*"
              element={<RepresentativeRoutes />}
            />
          )}
        </Route>
      </Route>
      <Route
        path="*"
        element={
          <Navigate
            to={`${user?.role === 'VOLUNTEER' ? '/volunteer' : `/${user?.role.toLowerCase()}`}/dashboard`}
            replace
          />
        }
      />
    </Routes>
  );
};

const App = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      // Fetch user info from backend
      api
        .get("/auth/me")
        .then((response) => {
          dispatch(setUser(response.data.user));
        })
        .catch(() => {
          // If error, clear auth state and token
          dispatch(clearAuth());
          localStorage.removeItem("token");
        });
    }
  }, [dispatch]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#1890ff",
        },
      }}
    >
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default App;
